import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, matchPath } from "react-router-dom";
import { useSession } from "../App";
import { usePermissions } from "../hooks/usePermissions";
import { ROUTES, allows } from "../config/permissions";
import JManWidget from "./JManWidget";
import NewVersionNotice, { useNewBuild } from "./NewVersionNotice";

// ── Route gate ───────────────────────────────────────────────────────────────
// Every gated route renders through this Layout's <Outlet/>, so guarding here
// guards all of them at once. That is deliberate: the previous design needed each
// component to remember to guard itself, and 10 of them didn't — Coach, Hiring,
// QBAudit and friends rendered P&L, resume PII and QuickBooks data to anyone who
// typed the URL. One choke point can't be forgotten.
//
// An unmatched path DENIES. scripts/check-route-registry.js (prebuild) makes that
// impossible to hit by accident, but if it ever happens the failure is closed.
function resolveRouteEntry(pathname) {
  if (ROUTES[pathname]) return ROUTES[pathname];
  // Parameterised routes (/quote/:id, /assess/smart/:id) — registry keys are the
  // same patterns App.jsx declares, so match them the way the router would.
  for (const key of Object.keys(ROUTES)) {
    if (key.indexOf(":") !== -1 && matchPath({ path: key, end: true }, pathname)) {
      return ROUTES[key];
    }
  }
  return null;
}

function RouteDenied() {
  return (
    <div style={{
      minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center",
      padding: 32, fontFamily: "'Roboto',sans-serif", textAlign: "center",
    }}>
      <div style={{ maxWidth: 380 }}>
        <div style={{
          fontFamily: "'Bebas Neue',sans-serif", fontSize: 44, letterSpacing: ".04em",
          color: "#a5ce3a", lineHeight: 1, marginBottom: 10,
        }}>
          No Access
        </div>
        <div style={{ color: "#8892a4", fontSize: 14, lineHeight: 1.7 }}>
          You don't have permission to view this page. If you need it, ask Dylan to
          grant access in Manage Crew.
        </div>
      </div>
    </div>
  );
}

// ── Nav Icons ─────────────────────────────────────────────────────────────────
function RequestsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="8" height="4" x="8" y="2" rx="1"/>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
      <line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/>
    </svg>
  );
}
function QuotesIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/>
    </svg>
  );
}
function CalcIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="16" height="20" x="4" y="2" rx="2"/>
      <line x1="8" y1="6" x2="16" y2="6"/>
      <line x1="8" y1="10" x2="10" y2="10"/><line x1="12" y1="10" x2="14" y2="10"/><line x1="16" y1="10" x2="16" y2="10"/>
      <line x1="8" y1="14" x2="10" y2="14"/><line x1="12" y1="14" x2="14" y2="14"/><line x1="16" y1="14" x2="16" y2="14"/>
      <line x1="8" y1="18" x2="10" y2="18"/><line x1="12" y1="18" x2="16" y2="18"/>
    </svg>
  );
}
function SideCrewIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
}
function SideJobsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="6" height="6" x="3" y="3" rx="1"/>
      <rect width="6" height="6" x="15" y="3" rx="1"/>
      <rect width="6" height="6" x="3" y="15" rx="1"/>
      <rect width="6" height="6" x="15" y="15" rx="1"/>
    </svg>
  );
}
function SideExpenseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/>
    </svg>
  );
}
function SideReconcileIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}
function QbAuditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <path d="M9 13l2 2 4-4"/>
    </svg>
  );
}
function SideAutoFillIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  );
}
function SideManageCrewIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <line x1="19" x2="19" y1="8" y2="14"/>
      <line x1="22" x2="16" y1="11" y2="11"/>
    </svg>
  );
}
function SideFeedbackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
}
function SideBrainIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/>
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/>
    </svg>
  );
}
function SideCoachIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="20" x2="12" y2="10"/>
      <line x1="18" y1="20" x2="18" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  );
}
function FleetIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 3h11v13H1z"/>
      <path d="M12 8h4.5L21 11.5V16h-9z"/>
      <circle cx="5.5" cy="18.5" r="2"/>
      <circle cx="17.5" cy="18.5" r="2"/>
    </svg>
  );
}

function SideHiringIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <polyline points="16 11 18 13 22 9"/>
    </svg>
  );
}
function SideMarketingIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11l18-5v12L3 14v-3z"/>
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>
    </svg>
  );
}
function SideAdminIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}
function SidePriceListIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
      <line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
  );
}
function HomeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  );
}
function ScheduleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="18" height="18" x="3" y="4" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
      <path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/><path d="M8 18h.01"/><path d="M12 18h.01"/>
    </svg>
  );
}
// SOPs — an open book.
function SopIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4h6a4 4 0 0 1 4 4v13a3 3 0 0 0-3-3H2z"/><path d="M22 4h-6a4 4 0 0 0-4 4v13a3 3 0 0 1 3-3h7z"/>
    </svg>
  );
}
function SideSopIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4h6a4 4 0 0 1 4 4v13a3 3 0 0 0-3-3H2z"/><path d="M22 4h-6a4 4 0 0 0-4 4v13a3 3 0 0 1 3-3h7z"/>
    </svg>
  );
}
function SideScheduleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="18" height="18" x="3" y="4" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
      <path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/>
    </svg>
  );
}
function JobsIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="8" height="4" x="8" y="2" rx="1"/>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
      <path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/>
    </svg>
  );
}
function ToolsIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
    </svg>
  );
}
function CrewIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
}
function MoreIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" x2="20" y1="6" y2="6"/>
      <line x1="4" x2="20" y1="12" y2="12"/>
      <line x1="4" x2="20" y1="18" y2="18"/>
    </svg>
  );
}

// ── More-overlay item icons ───────────────────────────────────────────────────
function ReconcileIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="9" y1="13" x2="15" y2="13"/>
      <line x1="9" y1="17" x2="13" y2="17"/>
    </svg>
  );
}
function ExpenseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/>
    </svg>
  );
}
function ManageCrewIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <line x1="19" x2="19" y1="8" y2="14"/>
      <line x1="22" x2="16" y1="11" y2="11"/>
    </svg>
  );
}
function FeedbackIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
}
function ProfitIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
      <polyline points="16 7 22 7 22 13"/>
    </svg>
  );
}
function CoachIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="20" x2="12" y2="10"/>
      <line x1="18" y1="20" x2="18" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  );
}
function SocialIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="3"/>
      <path d="M10 9l5 3-5 3V9z"/>
    </svg>
  );
}
function SideSocialIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="3"/>
      <path d="M10 9l5 3-5 3V9z"/>
    </svg>
  );
}
function MarketingIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11l18-5v12L3 14v-3z"/>
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>
    </svg>
  );
}
function HiringIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <polyline points="16 11 18 13 22 9"/>
    </svg>
  );
}
function PriceListIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
      <line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
  );
}
function SettingsIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}
function BrainIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/>
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/>
    </svg>
  );
}
function AutoFillIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="9" x2="15" y1="13" y2="13"/>
      <line x1="9" x2="11" y1="17" y2="17"/>
      <polyline points="17 17 19 19 23 15"/>
    </svg>
  );
}

// ElecBid Pipeline — command center + comms scheduler (layers/flow icon)
function SidePipelineIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2 2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
    </svg>
  );
}
function PipelineIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2 2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
    </svg>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
export default function Layout() {
  const { session } = useSession() || {};
  const perms = usePermissions() || {};
  const {
    role, permissions,
    realIsOwner, impersonating, viewAsEmail, setViewAsEmail, crewMember,
    featureOn, isSuperadmin,
  } = perms;

  // Per-company feature gating: a tab only renders if the company has that
  // feature on (Browning = all on). Maps each nav path to its feature key.
  const FEATURE_BY_PATH = {
    "/": "requests", "/schedule": "schedule", "/generator-report": "generator_report",
    "/pipeline": "pipeline", "/quotes": "quotes", "/takeoff": "quotes", "/quote-templates": "quote_templates", "/generator-playbook": "quotes",
    "/pricelist": "pricelist", "/calculators": "calculators", "/panel-schedule": "calculators", "/crew": "crew",
    "/jobs": "jobs", "/expenses": "expenses", "/reconciliation": "reconciliation",
    "/bookkeeper": "bookkeeper", "/payments": "payments", "/invoice-reminders": "invoice_reminders",
    "/qb-audit": "qb_audit", "/coach": "coach", "/coach/sell-rate": "coach", "/marketing": "marketing",
    "/hiring": "hiring", "/fleet": "fleet", "/social": "social",
    "/autofill": "autofill", "/manage-crew": "manage_crew", "/feedback": "feedback",
    "/jaiman-learning": "jaiman", "/admin": "admin",
  };
  const featureAllowed = (to) => {
    const key = FEATURE_BY_PATH[to];
    return key && typeof featureOn === "function" ? featureOn(key) : true;
  };
  const location = useLocation();
  const navigate = useNavigate();
  const userEmail = session?.user?.email?.toLowerCase() || "";
  const [moreOpen, setMoreOpen] = useState(false);
  // A newer build is deployed than the one this window runs (NewVersionNotice).
  const newBuild = useNewBuild();

  // ── Access, from the registry — NOT from hand-written show: expressions ─────
  // Every `show:` below used to be its own bespoke boolean, which is how the nav
  // and the routes drifted apart (a hidden link over an open page). Both now read
  // src/config/permissions.js, so a tab and its route can't disagree.
  //
  // Two views of the same user, on purpose:
  //  • navPerms pins realIsOwner=false so "View As X" stays a FAITHFUL preview —
  //    the owner sees exactly X's nav, Manage Crew included (i.e. stripped).
  //    isSuperadmin is pinned false the same way while impersonating: it comes
  //    from the REAL crew row (usePermissions), so without this the owner still
  //    saw super-only tabs (Companies) under a crew member's preview.
  //  • routePerms keeps realIsOwner so the real owner can still reach
  //    /manage-crew by URL while impersonating and get their exit hatch back.
  //    (ManageCrew.jsx guards on realIsOwner for this same reason.)
  const navPerms = { role, permissions, isSuperadmin: impersonating ? false : isSuperadmin, realIsOwner: false };
  const routePerms = { role, permissions, isSuperadmin, realIsOwner };
  const canSee = (to) => allows(ROUTES[to], navPerms) && featureAllowed(to);

  // Only the bottom (mobile) nav still needs these as standalone booleans.
  const canSeeCrew = canSee("/crew");
  const canViewCalculators = canSee("/calculators");

  // ── The gate itself ────────────────────────────────────────────────────────
  const routeEntry = resolveRouteEntry(location.pathname);
  const routeAllowed = allows(routeEntry, routePerms)
    && (routeEntry ? featureAllowed(location.pathname) : false);

  // Items that live in the "More" drawer.
  // `show:` is gone on purpose — visibility comes from canSee(to), i.e. the
  // registry. Manage Crew keeps one extra condition (!impersonating) so View As
  // stays a faithful preview; that is a nav-presentation rule, not an access rule,
  // which is why it lives here and not in the registry.
  const moreItems = [
    { to: "/schedule",        label: "My Schedule",        description: "Your visits & tasks, notes + photos, clock in/out", Icon: ScheduleIcon },
    { to: "/sops",            label: "SOPs",               description: "How we do things — note prompts, daily routine, step by step", Icon: SopIcon },
    { to: "/generator-report", label: "Generator Maintenance", description: "Annual standby-generator reports, equipment & service due", Icon: ScheduleIcon },
    { to: "/pipeline",       label: "ElecBid Pipeline",   description: "Command center + scheduled client texts & emails", Icon: PipelineIcon },
    { to: "/takeoff",         label: "Material Takeoff",   description: "Per-job material lists, ordering & van stock", Icon: PriceListIcon },
    { to: "/generator-playbook", label: "Generator Playbook", description: "Sales training: real generator installs by scenario, photos, price, hours, GP + FAQ", Icon: SopIcon },
    { to: "/panel-schedule",  label: "Panel Schedule",     description: "Circuit directory from a /panel note — sized PDF/PNG for the door label", Icon: PriceListIcon },
    { to: "/jobs",            label: "Jobs",               description: "All jobs from Jobber — edit & push back",    Icon: SideJobsIcon },
    { to: "/expenses",        label: "Expenses",           description: "Invoice intelligence & expense tracking",   Icon: ExpenseIcon },
    { to: "/reconciliation",  label: "Reconciliation",     description: "Supplier statement reconciliation",          Icon: ReconcileIcon },
    { to: "/bookkeeper",      label: "Bookkeeper",         description: "Autonomous expense reconciliation & triage", Icon: QbAuditIcon },
    { to: "/payments",        label: "Payments",           description: "e-Transfers matched to Jobber invoices",     Icon: QbAuditIcon },
    { to: "/invoice-reminders", label: "Invoice Reminders", description: "Payment reminders from our own mailbox — PO + invoice attached", Icon: QbAuditIcon },
    { to: "/raffle/admin",    label: "Raffle Tickets",     description: "Gemstone raffle for Shuswap Hospice — tickets, e-transfers, draw-day lookup", Icon: QbAuditIcon },
    { to: "/qb-audit",        label: "QB Audit",           description: "Compare AP emails to QuickBooks Bills",      Icon: QbAuditIcon },
    { to: "/coach",           label: "Coach",              description: "Monthly variance, balance sheet, budget",    Icon: CoachIcon },
    { to: "/marketing",       label: "Marketing",          description: "Command center: ad spend, leads, ROAS, agency review", Icon: MarketingIcon },
    { to: "/comms",           label: "Communications",     description: "Every customer call, text and email across Quo, all mailboxes & Jobber", Icon: MarketingIcon },
    { to: "/social",          label: "Social Studio",      description: "AI content studio — titles, descriptions, hashtags & post times", Icon: SocialIcon },
    { to: "/hiring",          label: "Hiring",             description: "Resume inbox & candidate pipeline",          Icon: HiringIcon },
    { to: "/fleet",           label: "Fleet",              description: "Vans, documents & maintenance schedules",    Icon: FleetIcon },
    { to: "/pricelist",      label: "Price List",        description: "Parts pricing & materials catalogue",       Icon: PriceListIcon },
    { to: "/autofill",       label: "Job Auto-Fill",     description: "Push crew metrics to Jobber custom fields", Icon: AutoFillIcon },
    { to: "/manage-crew",    label: "Manage Crew",       description: "Permissions, roles & crew settings",        Icon: ManageCrewIcon, show: !impersonating },
    { to: "/feedback",       label: "Feedback",          description: "Client & team feedback hub",                Icon: FeedbackIcon },
    { to: "/jaiman-learning", label: "Jai-Man Learning",  description: "What Jai-Man remembers and has learned",     Icon: BrainIcon },
    { to: "/admin",          label: "Admin",             description: "Sync controls & service diagnostics",       Icon: SettingsIcon },
  ].filter(t => t.show !== false && canSee(t.to));

  const morePaths = moreItems.map(i => i.to);
  const isMoreActive = morePaths.some(p => location.pathname === p || location.pathname.startsWith(p + "/"));

  // Desktop sidebar — organised into a handful of CATEGORIES instead of ~20 flat
  // links. A group (with `children`) renders as a hover-expandable submenu that
  // also auto-expands when the active route is inside it. Two prominent daily
  // surfaces (Requests, the Pipeline command center) stay top-level.
  //
  // SECURITY: children are gated by canSee() (registry tier + permission +
  // company feature), NOT just featureAllowed — the same gate a flat leaf uses.
  // A group with no visible child is dropped entirely, so a crew member never
  // sees an empty "Money"/"System" header teasing tabs they can't open.
  // Dylan 2026-07-28: Schedule holds the prominent slot under Requests — the
  // crew's day is the first thing anyone needs. Pipeline (admin command center)
  // moved down beside Marketing, unchanged otherwise.
  const rawSideItems = [
    { to: "/",         label: "Requests",        end: true, Icon: RequestsIcon },
    { to: "/schedule", label: "Schedule",                   Icon: SideScheduleIcon },
    { to: "/sops",     label: "SOPs",                       Icon: SideSopIcon },
    { to: "/generator-report", label: "Generator Maintenance", Icon: SideScheduleIcon },
    { label: "Estimating", Icon: QuotesIcon, children: [
      { to: "/quotes",      label: "Quotes",          Icon: QuotesIcon },
      { to: "/generator-playbook", label: "Generator Playbook", Icon: SideSopIcon },
      { to: "/takeoff",     label: "Material Takeoff", Icon: SidePriceListIcon },
      { to: "/pricelist",   label: "Price List",      Icon: SidePriceListIcon },
      { to: "/calculators", label: "Calculators",     Icon: CalcIcon },
      { to: "/panel-schedule", label: "Panel Schedule", Icon: CalcIcon },
    ] },
    { label: "Jobs", Icon: SideJobsIcon, children: [
      { to: "/jobs",     label: "Jobs",          Icon: SideJobsIcon },
      { to: "/autofill", label: "Job Auto-Fill", Icon: SideAutoFillIcon },
      { to: "/fleet",    label: "Fleet",         Icon: FleetIcon },
    ] },
    { label: "Money", Icon: SideCoachIcon, children: [
      { to: "/expenses",       label: "Expenses",       Icon: SideExpenseIcon },
      { to: "/reconciliation", label: "Reconciliation", Icon: SideReconcileIcon },
      { to: "/bookkeeper",     label: "Bookkeeper",     Icon: QbAuditIcon },
      { to: "/payments",       label: "Payments",       Icon: QbAuditIcon },
      { to: "/invoice-reminders", label: "Invoice Reminders", Icon: QbAuditIcon },
      { to: "/raffle/admin",   label: "Raffle Tickets", Icon: QbAuditIcon },
      { to: "/qb-audit",       label: "QB Audit",       Icon: QbAuditIcon },
      { to: "/coach",          label: "Coach",          Icon: SideCoachIcon },
    ] },
    { label: "Team", Icon: SideCrewIcon, children: [
      { to: "/crew",        label: "Crew Performance", Icon: SideCrewIcon },
      { to: "/manage-crew", label: "Manage Crew", show: !impersonating, Icon: SideManageCrewIcon },
      { to: "/hiring",      label: "Hiring",           Icon: SideHiringIcon },
    ] },
    { to: "/pipeline", label: "ElecBid Pipeline", Icon: SidePipelineIcon },
    { to: "/marketing", label: "Marketing", Icon: SideMarketingIcon },
    { to: "/comms", label: "Communications", Icon: SideMarketingIcon },
    { to: "/social", label: "Social Studio", Icon: SideSocialIcon },
    { label: "System", Icon: SideAdminIcon, children: [
      { to: "/feedback",        label: "Feedback",         Icon: SideFeedbackIcon },
      { to: "/jaiman-learning", label: "Jai-Man Learning", Icon: SideBrainIcon },
      { to: "/companies",       label: "Companies",        Icon: SideAdminIcon },
      { to: "/admin",           label: "Admin",            Icon: SideAdminIcon },
    ] },
  ];
  const sideItems = rawSideItems
    .map((item) => {
      if (Array.isArray(item.children)) {
        const kids = item.children.filter((c) => c.show !== false && canSee(c.to));
        return kids.length ? { ...item, children: kids } : null;
      }
      return (item.show !== false && canSee(item.to)) ? item : null;
    })
    .filter(Boolean);

  // Active-state helpers (for paths that span multiple routes)
  const isHomeActive  = location.pathname === "/" || location.pathname.startsWith("/assess");
  const isToolsActive = location.pathname === "/calculators" || location.pathname === "/pricelist" || location.pathname.startsWith("/panel-schedule");

  return (
    <>
      <style>{LAYOUT_CSS}</style>
      <div className="layout-shell">

        {/* ── Mobile header (branding, mobile only) ── */}
        <div className="mobile-header">
          <div className="brand-area">
            <img src="/elecbid-icon-128.png" alt="ElecBid" className="brand-icon" />
            <span className="brand-wordmark">
              <span className="brand-elec">Elec</span><span className="brand-bid">Bid</span>
            </span>
          </div>
          <span className="mobile-header-company">Browning Electric</span>
        </div>

        {/* ── Desktop sidebar ── */}
        <nav className="side-nav" aria-label="Main navigation">
          <div className="side-nav-brand">
            <div className="brand-area">
              <img src="/elecbid-icon-128.png" alt="ElecBid" className="brand-icon" />
              <span className="brand-wordmark">
                <span className="brand-elec">Elec</span><span className="brand-bid">Bid</span>
              </span>
            </div>
            <div className="side-brand-company">Browning Electric</div>
          </div>
          {sideItems.map((item, idx) => {
            // Group with children: hover-expandable submenu, also auto-expands
            // when the active route is one of its children.
            if (Array.isArray(item.children)) {
              const kids = item.children; // already gated by canSee() above
              const groupActive = kids.some(c =>
                location.pathname === c.to || location.pathname.startsWith(c.to + "/")
              );
              return (
                <div key={`group_${idx}`} className={`side-nav-group${groupActive ? " active" : ""}`}>
                  <div className="side-nav-link side-nav-group-head" tabIndex={0}>
                    <item.Icon />
                    {item.label}
                    <span className="side-nav-chev">›</span>
                  </div>
                  <div className="side-nav-submenu">
                    {kids.map(c => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        end={c.end}
                        className={({ isActive }) => `side-nav-sublink${isActive ? " active" : ""}`}
                      >
                        {c.Icon ? <c.Icon /> : null}
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `side-nav-link${isActive ? " active" : ""}`}
              >
                <item.Icon />
                {item.label}
              </NavLink>
            );
          })}
          {/* Renders nothing until a newer build is deployed. Desktop copy. */}
          <NewVersionNotice className="side-nav-update" stacked />
        </nav>

        {/* ── Main content ── */}
        {/* The single route gate. Nav still renders so a denied user isn't stranded. */}
        <main className={`layout-content${newBuild ? " has-update" : ""}`}>
          {routeAllowed ? <Outlet /> : <RouteDenied />}
        </main>

        <JManWidget
          currentPath={location.pathname}
          userEmail={impersonating && viewAsEmail ? viewAsEmail.toLowerCase() : userEmail}
          readOnly={impersonating}
        />

        {/* ── "More" full-screen overlay ── */}
        {moreOpen && (
          <div className="more-overlay" onClick={() => setMoreOpen(false)}>
            <div className="more-panel" onClick={e => e.stopPropagation()}>
              <div className="more-header">
                <span className="more-title">More</span>
                <button className="more-close" onClick={() => setMoreOpen(false)}>✕</button>
              </div>
              {moreItems.map(item => (
                <button
                  key={item.to}
                  className={`more-item${location.pathname === item.to ? " active" : ""}`}
                  onClick={() => { navigate(item.to); setMoreOpen(false); }}
                >
                  <span className="more-item-icon"><item.Icon /></span>
                  <span className="more-item-info">
                    <span className="more-item-label">{item.label}</span>
                    <span className="more-item-desc">{item.description}</span>
                  </span>
                  <span className="more-item-arrow">›</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Bottom nav (mobile) ── */}
        <nav className="bottom-nav" aria-label="Main navigation">
          <NavLink to="/" end className={() => `bottom-nav-item${isHomeActive ? " active" : ""}`}>
            <HomeIcon />
            <span className="bottom-nav-label">Requests</span>
          </NavLink>

          <NavLink to="/schedule" className={({ isActive }) => `bottom-nav-item${isActive ? " active" : ""}`}>
            <ScheduleIcon />
            <span className="bottom-nav-label">Schedule</span>
          </NavLink>

          <NavLink to="/quotes" className={({ isActive }) => `bottom-nav-item${isActive ? " active" : ""}`}>
            <JobsIcon />
            <span className="bottom-nav-label">Quotes</span>
          </NavLink>

          {canViewCalculators && (
            <NavLink to="/calculators" className={() => `bottom-nav-item${isToolsActive ? " active" : ""}`}>
              <ToolsIcon />
              <span className="bottom-nav-label">Tools</span>
            </NavLink>
          )}

          {canSeeCrew && (
            <NavLink to="/crew" className={({ isActive }) => `bottom-nav-item${isActive ? " active" : ""}`}>
              <CrewIcon />
              <span className="bottom-nav-label">Crew</span>
            </NavLink>
          )}

          <button className={`bottom-nav-item${isMoreActive ? " active" : ""}`} onClick={() => setMoreOpen(true)}>
            <MoreIcon />
            <span className="bottom-nav-label">More</span>
          </button>
        </nav>

        {/* ── "View As" floating chip (owner-only, only when impersonating) ── */}
        {realIsOwner && impersonating && (
          <div className="viewas-chip">
            <span className="viewas-dot" />
            <span className="viewas-text">
              Viewing as <strong>{crewMember?.display_name || viewAsEmail}</strong>
              {crewMember?.jobber_employee_name ? ` · ${crewMember.jobber_employee_name}` : ""}
            </span>
            <button className="viewas-exit" onClick={() => setViewAsEmail("")}>
              Exit
            </button>
          </div>
        )}

        {/* ── "New version available" (phone copy; the desktop one is in the sidebar) ── */}
        <NewVersionNotice className={`update-float${realIsOwner && impersonating ? " above-chip" : ""}`} />

      </div>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
const LAYOUT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Roboto:wght@400;500;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent}
  :root{
    --bg:#0d0f12;--surface:#161a20;--surface2:#1e2328;--border:#2a3140;
    --accent:#a5ce3a;--accent-dim:rgba(165,206,58,.12);
    --text:#e8eaf0;--text-muted:#8892a4;
    --nav-h:60px;--side-w:200px
  }
  html,body,#root{height:100%;margin:0}
  body{background:var(--bg);color:var(--text);font-family:'Roboto',sans-serif}

  /* Shell */
  .layout-shell{display:flex;flex-direction:column;min-height:100vh;background:var(--bg)}
  .layout-content{flex:1;overflow-y:auto;padding-top:48px;padding-bottom:calc(var(--nav-h) + env(safe-area-inset-bottom,0px) + 8px)}

  /* Mobile header */
  .mobile-header{
    position:fixed;top:0;left:0;right:0;height:48px;
    display:flex;align-items:center;justify-content:space-between;
    padding:0 16px;background:var(--bg);border-bottom:1px solid var(--border);
    z-index:40
  }
  .brand-area{display:flex;align-items:center;gap:8px}
  .brand-icon{width:22px;height:22px;border-radius:4px;object-fit:cover;flex-shrink:0}
  .brand-wordmark{
    font-family:'Bebas Neue',sans-serif;font-size:19px;letter-spacing:.06em;line-height:1
  }
  .mobile-header-company{font-size:10px;color:rgba(255,255,255,0.38);font-weight:400;letter-spacing:.02em}
  .brand-elec{color:#e8eaf0;font-weight:700}
  .brand-bid{color:var(--accent);font-weight:700}

  /* View-as floating chip — bottom-right, doesn't block sidebar/tabs */
  .viewas-chip{
    position:fixed;
    right:16px;
    bottom:calc(var(--nav-h) + env(safe-area-inset-bottom,0px) + 12px);
    z-index:90;
    display:flex;align-items:center;gap:8px;
    padding:8px 10px 8px 12px;
    background:#fbbf24;color:#181818;
    border-radius:999px;
    border:1px solid #b45309;
    box-shadow:0 6px 16px rgba(0,0,0,0.35);
    font-size:12px;font-weight:600;
    max-width:min(360px, calc(100vw - 32px))
  }
  .viewas-dot{
    width:7px;height:7px;border-radius:50%;background:#b45309;flex-shrink:0;
    box-shadow:0 0 0 0 rgba(180,83,9,0.6);animation:viewas-pulse 1.8s infinite
  }
  @keyframes viewas-pulse{
    0%{box-shadow:0 0 0 0 rgba(180,83,9,0.6)}
    70%{box-shadow:0 0 0 6px rgba(180,83,9,0)}
    100%{box-shadow:0 0 0 0 rgba(180,83,9,0)}
  }
  .viewas-text{
    line-height:1.25;
    overflow:hidden;text-overflow:ellipsis;white-space:nowrap
  }
  .viewas-exit{
    background:#181818;color:#fbbf24;border:none;border-radius:999px;
    padding:4px 10px;font-size:11px;font-weight:700;cursor:pointer;
    font-family:'Roboto',sans-serif;flex-shrink:0
  }
  .viewas-exit:hover{background:#000}
  @media(min-width:768px){
    .viewas-chip{bottom:16px}
  }

  /* New-version notice, phone copy (NewVersionNotice.jsx) — above the bottom
     bar and clear of the Jai-Man button on the right (56px wide, 20px in).
     z-index: over the bottom bar, under the page sheets and toasts (60 and up). */
  .update-float{
    position:fixed;left:12px;z-index:52;
    bottom:calc(var(--nav-h) + env(safe-area-inset-bottom,0px) + 12px);
    width:max-content;max-width:calc(100vw - 98px)
  }
  /* the View As chip sits on that line: go above it */
  .update-float.above-chip{bottom:calc(var(--nav-h) + env(safe-area-inset-bottom,0px) + 58px)}
  /* while the notice shows, a page can scroll its last rows clear of it */
  .layout-content.has-update{padding-bottom:calc(var(--nav-h) + env(safe-area-inset-bottom,0px) + 104px)}

  /* Bottom nav */
  .bottom-nav{
    position:fixed;bottom:0;left:0;right:0;
    height:calc(var(--nav-h) + env(safe-area-inset-bottom,0px));
    background:#111318;border-top:1px solid var(--border);
    display:flex;z-index:50;padding:0 4px;
    padding-bottom:env(safe-area-inset-bottom,0px)
  }
  .bottom-nav-item{
    flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;
    gap:3px;color:var(--text-muted);text-decoration:none;
    background:none;border:none;cursor:pointer;padding:0;
    font-family:'Roboto',sans-serif;transition:color 0.15s;
    -webkit-tap-highlight-color:transparent
  }
  .bottom-nav-item.active{color:var(--accent)}
  .bottom-nav-item svg{display:block;flex-shrink:0}
  .bottom-nav-label{font-size:10px;font-weight:600;letter-spacing:.03em;line-height:1}

  /* More overlay */
  .more-overlay{
    position:fixed;inset:0;background:rgba(0,0,0,0.72);
    z-index:100;display:flex;align-items:flex-end
  }
  .more-panel{
    width:100%;background:var(--surface);
    border-radius:20px 20px 0 0;border-top:1px solid var(--border);
    max-height:85vh;overflow-y:auto;-webkit-overflow-scrolling:touch;
    padding-bottom:env(safe-area-inset-bottom,0px)
  }
  .more-header{
    display:flex;align-items:center;justify-content:space-between;
    padding:20px 20px 16px;border-bottom:1px solid var(--border);
    position:sticky;top:0;background:var(--surface);z-index:1
  }
  .more-title{
    font-size:22px;font-weight:700;color:var(--text);
    font-family:'Bebas Neue',sans-serif;letter-spacing:.05em
  }
  .more-close{
    background:none;border:none;color:var(--text-muted);font-size:18px;
    cursor:pointer;padding:6px 10px;border-radius:8px;
    font-family:'Roboto',sans-serif;line-height:1
  }
  .more-item{
    width:100%;display:flex;align-items:center;gap:16px;
    padding:18px 20px;border:none;background:none;cursor:pointer;
    color:var(--text);text-align:left;
    border-bottom:1px solid rgba(42,49,64,0.5);
    transition:background 0.15s;-webkit-tap-highlight-color:transparent
  }
  .more-item:last-child{border-bottom:none}
  .more-item:active,.more-item:hover{background:var(--surface2)}
  .more-item.active{background:var(--accent-dim)}
  .more-item-icon{color:var(--text-muted);flex-shrink:0;display:flex}
  .more-item.active .more-item-icon{color:var(--accent)}
  .more-item-info{flex:1;display:flex;flex-direction:column;gap:3px;min-width:0}
  .more-item-label{font-size:16px;font-weight:600;color:var(--text)}
  .more-item.active .more-item-label{color:var(--accent)}
  .more-item-desc{font-size:12px;color:var(--text-muted)}
  .more-item-arrow{font-size:22px;color:var(--text-muted);font-weight:300;line-height:1;flex-shrink:0}

  /* Desktop sidebar — hidden on mobile */
  .side-nav{display:none}

  @media(min-width:768px){
    .layout-shell{flex-direction:row}
    .bottom-nav{display:none}
    .more-overlay{display:none}
    .mobile-header{display:none}
    .side-nav{
      display:flex;flex-direction:column;width:var(--side-w);height:100vh;
      background:var(--surface);border-right:1px solid var(--border);
      padding:20px 0;position:fixed;top:0;left:0;z-index:50;
      /* short screens: the nav scrolls internally instead of clipping the
         bottom items (Admin was unreachable on small laptops) */
      overflow-y:auto;overscroll-behavior:contain;scrollbar-width:thin;
      scrollbar-color:var(--border) transparent
    }
    .side-nav::-webkit-scrollbar{width:6px}
    .side-nav::-webkit-scrollbar-thumb{background:var(--border);border-radius:3px}
    .side-nav-brand{
      padding:16px 20px 14px;
      border-bottom:1px solid var(--border);
      margin-bottom:12px
    }
    .side-brand-company{
      font-size:10px;font-weight:400;color:rgba(255,255,255,0.35);
      letter-spacing:.02em;
      margin-top:8px
    }
    .side-nav-link{
      display:flex;align-items:center;gap:10px;padding:10px 20px;
      color:var(--text-muted);text-decoration:none;font-size:14px;font-weight:500;
      border-left:3px solid transparent
    }
    .side-nav-link.active{
      color:var(--accent);background:var(--accent-dim);border-left-color:var(--accent)
    }

    /* ─── Sidebar groups (QuickBooks-style hover submenus) ──────────────── */
    .side-nav-group{position:relative}
    .side-nav-group-head{cursor:default;user-select:none}
    .side-nav-group-head .side-nav-chev{margin-left:auto;font-size:14px;color:var(--text-muted);transition:transform .15s}
    .side-nav-submenu{
      display:none;
      flex-direction:column;
      background:var(--surface2);
      border-left:3px solid var(--accent-dim);
      margin-left:20px;
      padding:4px 0;
    }
    /* Always expand when one of the children is active */
    .side-nav-group.active .side-nav-submenu{display:flex}
    .side-nav-group.active .side-nav-group-head{color:var(--text)}
    .side-nav-group.active .side-nav-group-head .side-nav-chev{transform:rotate(90deg)}
    /* And expand on hover for quick browsing */
    .side-nav-group:hover .side-nav-submenu{display:flex}
    .side-nav-group:hover .side-nav-group-head .side-nav-chev{transform:rotate(90deg)}
    .side-nav-sublink{
      display:flex;align-items:center;gap:9px;
      padding:8px 20px 8px 28px;
      color:var(--text-muted);text-decoration:none;font-size:13px;font-weight:500
    }
    .side-nav-sublink svg{width:15px;height:15px;flex-shrink:0;opacity:.9}
    .side-nav-sublink:hover{color:var(--text)}
    .side-nav-sublink.active{color:var(--accent);background:var(--accent-dim)}

    /* New-version notice, desktop copy — at the foot of the sidebar. On a short
       screen it stays there and the links scroll under it, so each one can
       still be reached. */
    .side-nav-update{
      margin-top:auto;position:sticky;bottom:0;flex-shrink:0;
      padding:12px 12px 0;background:var(--surface)
    }
    /* a hovered group opens its submenu: it pushes the notice down, not under it */
    .side-nav-group:hover ~ .side-nav-update{position:static}
    .update-float{display:none}

    .layout-content{margin-left:var(--side-w);padding-top:0;padding-bottom:16px}
    .layout-content.has-update{padding-bottom:16px}
  }
`;
